import type { ReactElement } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { TooltipContent } from '@/components/ui/tooltip';
import { useTruncationObserver } from '@/hooks/useTruncationObserver';
import { cn } from '@/lib/utils';

const ASTERISK_ONLY = /^\*$/;

/**
 * Collapsed cell: Radix tooltip only when text is truncated (ellipsis); never for lone `*`.
 *
 * Expects an ancestor `TooltipProvider` (see `EditableRowTable`). Primitives are used so
 * content portals outside scroll areas; the shadcn `Tooltip` wrapper is not used here to
 * avoid nesting a second provider per trigger.
 */
export function CollapsedSummaryText({
  text,
  className,
}: {
  text: string;
  className: string;
}): ReactElement {
  const { truncationRef, isTruncated, checkTruncation } = useTruncationObserver(
    { subscribeWindowResize: false }
  );
  const showTip = isTruncated && !ASTERISK_ONLY.test(text.trim());
  const spanClass = cn('min-w-0 flex-1 truncate', className);

  return (
    <TooltipPrimitive.Root delayDuration={400}>
      <TooltipPrimitive.Trigger asChild>
        <span
          ref={truncationRef}
          className={spanClass}
          onMouseEnter={checkTruncation}
        >
          {text}
        </span>
      </TooltipPrimitive.Trigger>
      {showTip ? (
        <TooltipContent
          side="top"
          sideOffset={6}
          className="max-w-md break-words whitespace-pre-wrap leading-snug"
        >
          {text}
        </TooltipContent>
      ) : null}
    </TooltipPrimitive.Root>
  );
}
